import { useEffect, useState } from 'react';
import { Check, Copy, Download, Mail } from 'lucide-react';
import { Modal } from '../../Modal';
import { docSource } from '../../../lib/doc-viewer';
import { effectiveContent, parseFileRecordContent } from '../../../lib/file-record';
import { downloadArtifact } from '../../../lib/artifact-file';
import { nameOf, type BrowsableFile } from '../../../lib/file-browser';
import { useTranslation } from '../../../lib/i18n';
import { Button, Input } from '../ui';
import styles from './ShareFileDialog.module.css';

// "Share" on a file row. What it offers depends on whether the file HAS a link:
//
//  - **It does** (its bytes live in Wix Media) → that URL, ready to copy. The link to the FILE, never a link to
//    a page of ours: a recipient who is handed a Symphony URL cannot open it without a Symphony account, which
//    is not sharing a file.
//  - **It doesn't** (a markdown note, a CSV written as content — the body IS the file) → there is no link to
//    give, so the dialog offers the two things that do work: download it, or open a mail draft with it. Showing
//    a page URL here was worse than showing nothing, because it looked like a share link and wasn't one.
//
// ⚠️ **A media URL is TOKEN-SIGNED and expires**, so a shared link rots — the recipient gets a 403 some time
// later. Sharing it anyway is deliberate and product-owned (it opens the actual file, in anything, with no
// Symphony account); the expiry is a real problem to fix at the source. This is the one place that changes when
// the backend can mint a durable link. The dialog does NOT explain any of this to the reader — a person sharing
// a file does not want a paragraph about our storage model.
export function ShareFileDialog({ item, onClose }: { item: BrowsableFile; onClose: () => void }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  // The stored file's real location, resolved the same way the viewer resolves it (a promoted file carries a
  // FileRecord header, so this is never a naive read of `content`).
  const source = docSource(effectiveContent(item));
  const url = source?.kind === 'remote' ? source.url : null;

  // The confirmation is transient, and it must not outlive the dialog it belongs to.
  useEffect(() => {
    if (!copied) return undefined;
    const timer = window.setTimeout(() => setCopied(false), 2000);
    return () => window.clearTimeout(timer);
  }, [copied]);

  const copy = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      // A denied clipboard is not worth a dialog of its own — the field is selectable, so the link is still
      // takeable by hand.
      setCopied(false);
    }
  };

  // A linkless file's body IS the file, so the mail draft carries it inline. Trimmed to a length a `mailto:`
  // survives — browsers and mail clients cap the URL, and a silently-truncated draft is worse than a short one
  // that says the whole file came down with Download.
  const emailDraft = () => {
    const { body } = parseFileRecordContent(effectiveContent(item));
    const subject = encodeURIComponent(nameOf(item.name));
    const trimmed = body.length > MAIL_BODY_LIMIT ? `${body.slice(0, MAIL_BODY_LIMIT)}\n…` : body;
    window.location.href = `mailto:?subject=${subject}&body=${encodeURIComponent(trimmed)}`;
  };

  return (
    <Modal
      open
      onClose={onClose}
      size="minimum"
      maxHeightPx={650}
      scrollBody
      headerSeparator
      title={t('files.share.title')}
      description={url ? t('files.share.description', { file: nameOf(item.name) }) : t('files.share.noLink', { file: nameOf(item.name) })}
      footer={
        url ? (
          <Button onClick={() => void copy()} startIcon={copied ? <Check size={16} aria-hidden /> : <Copy size={16} aria-hidden />}>
            {copied ? t('files.share.copied') : t('files.share.copy')}
          </Button>
        ) : undefined
      }
    >
      <div className={styles.body}>
        {url ? (
          /* Read-only and select-on-focus rather than a line of text: on a phone there is no "copy link" for
             text that is not in a field, and a truncated line you cannot scroll is not a link you can share. */
          <Input value={url} readOnly aria-label={t('files.share.title')} onFocus={(e) => e.currentTarget.select()} />
        ) : (
          /* The two actions sit in the BODY here rather than the footer: with no link field above them, a footer
             would leave an empty content region between the description and the buttons. */
          <div className={styles.actions}>
            <Button variant="outlined" onClick={emailDraft} startIcon={<Mail size={16} aria-hidden />}>
              {t('files.share.email')}
            </Button>
            <Button onClick={() => void downloadArtifact(item as never)} startIcon={<Download size={16} aria-hidden />}>
              {t('packs.download')}
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
}

/** How much of a linkless file's body the mail draft carries. `mailto:` URLs are capped by the browser and by
 *  the mail client, and the caps differ, so this stays well under the smallest of them. */
const MAIL_BODY_LIMIT = 1500;
